import { Box, CardActionArea, Paper } from "@mui/material";
import { alpha, styled } from "@mui/material/styles";
import { contorno, cores, raio, superficie } from "../../../components/atendimento/identidade";

const ALTURA_HEADER = 50;

const naoRepassar = (prop: PropertyKey) => prop !== "alerta" && prop !== "selecionado";

export const PageContainer = styled(Box)({
    width: "100%",
    height: "100%",
    display: "flex",
    maxHeight: `calc(100vh - ${ALTURA_HEADER}px)`,
    overflow: "hidden",
});

export const ChatsContainer = styled(Paper)(({ theme }) => ({
    width: 340,
    flexShrink: 0,
    borderRadius: 0,
    borderTop: 0,
    borderLeft: 0,
    borderRight: contorno(theme, 0.09),
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    maxHeight: "100%",
    minHeight: `calc(100vh - ${ALTURA_HEADER}px)`,
    overflowY: "auto",
    backgroundImage: "none",
    "&::-webkit-scrollbar": { width: 6 },
    "&::-webkit-scrollbar-track": { background: "transparent" },
    "&::-webkit-scrollbar-thumb": {
        borderRadius: 8,
        backgroundColor: alpha(theme.palette.text.secondary, 0.22),
    },
    "&:hover::-webkit-scrollbar-thumb": {
        backgroundColor: alpha(theme.palette.text.secondary, 0.38),
    },
}));

export const PesquisarContainer = styled(Box)(({ theme }) => ({
    width: "100%",
    padding: theme.spacing(1.5, 1.75, 1),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.25),
}));

/** Caixa de busca: mesmo desenho da landing — fundo em overlay, sem elevação. */
export const CaixaBusca = styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    height: 38,
    padding: theme.spacing(0, 1.25),
    borderRadius: raio.sm,
    backgroundColor: superficie(theme, 0.05),
    border: contorno(theme, 0.08),
    transition: theme.transitions.create(["border-color", "background-color"], { duration: 160 }),
    "&:focus-within": {
        borderColor: alpha(cores.marca[500], 0.45),
        backgroundColor: alpha(cores.marca[500], 0.05),
    },
}));

interface CardChatProps {
    alerta?: boolean;
    selecionado?: boolean;
}

/**
 * Card da conversa. A faixa lateral colorida é o que faz o operador bater o
 * olho e saber o estado sem ler: amarela quando a IA pediu ajuda, azul quando
 * está em atendimento.
 */
export const CardChat = styled(Box, { shouldForwardProp: naoRepassar })<CardChatProps>(
    ({ theme, alerta, selecionado }) => ({
        position: "relative",
        width: "100%",
        flexShrink: 0,
        borderBottom: contorno(theme, 0.06),
        transition: theme.transitions.create("background-color", { duration: 160 }),
        backgroundColor: selecionado
            ? superficie(theme, 0.07)
            : alerta
                ? alpha(cores.alerta[500], theme.palette.mode === "dark" ? 0.1 : 0.07)
                : "transparent",
        "&:hover": {
            backgroundColor: selecionado ? superficie(theme, 0.08) : superficie(theme, 0.045),
        },
        "&::before": {
            content: '""',
            position: "absolute",
            left: 0,
            top: 6,
            bottom: 6,
            width: 3,
            borderRadius: "0 3px 3px 0",
            backgroundColor: "transparent",
        },
    })
);

export const CardActionAreaChat = styled(CardActionArea)(({ theme }) => ({
    width: "100%",
    display: "flex",
    justifyContent: "flex-start",
    alignItems: "flex-start",
    padding: theme.spacing(1.5, 1.75),
    gap: theme.spacing(1.25),
    borderRadius: 0,
}));

export const CardChatMensagemContainer = styled(Box)({
    display: "flex",
    flexDirection: "column",
    gap: 2,
    alignItems: "flex-start",
    flex: 1,
    minWidth: 0,
});

export const MensagensContainer = styled(Box)({
    display: "flex",
    flexDirection: "column",
    flex: 1,
    height: "100%",
    minHeight: `calc(100vh - ${ALTURA_HEADER}px)`,
    maxHeight: `calc(100vh - ${ALTURA_HEADER}px)`,
    overflow: "hidden",
    minWidth: 0,
});

export const MensagensHeaderContainer = styled(Paper)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
    border: 0,
    borderBottom: contorno(theme, 0.09),
    borderRadius: 0,
    padding: theme.spacing(1.5, 2),
    backgroundImage: "none",
    flexShrink: 0,
}));

export const MensagensContatoContainer = styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    gap: theme.spacing(1.5),
    width: "100%",
    minWidth: 0,
}));

export const DadosContatoContainer = styled(Box)({
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
});

/**
 * Coluna da conversa. Fundo limpo (o papel do tema), como na landing: as bolhas
 * já são tints sobre ele, então qualquer tom aqui embaixo suja o contraste.
 */
export const SessaoMensagensContainer = styled(Box)(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    backgroundColor: theme.palette.background.paper,
    padding: theme.spacing(2.5, 3),
    gap: theme.spacing(1.25),
    width: "100%",
    flex: 1,
    overflowY: "auto",
    "&::-webkit-scrollbar": { width: 6 },
    "&::-webkit-scrollbar-track": { background: "transparent" },
    "&::-webkit-scrollbar-thumb": {
        borderRadius: 8,
        backgroundColor: alpha(theme.palette.text.secondary, 0.22),
    },
}));

/** Rótulo de seção em caixa alta — usado nos cabeçalhos das colunas. */
export const RotuloSecao = styled(Box)(({ theme }) => ({
    fontSize: "11px",
    fontWeight: 800,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: theme.palette.text.secondary,
}));
